import { useRef, useState, useCallback } from 'react'
import { UploadCloud, FileSpreadsheet } from 'lucide-react'

interface SpreadsheetUploadZoneProps {
  onUpload: (file: File) => Promise<void>
  disabled?: boolean
}

const ACCEPTED_TYPES = ['.csv', '.xlsx', '.xlsm']
const ACCEPT_MIME = 'text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'

export function SpreadsheetUploadZone({ onUpload, disabled = false }: SpreadsheetUploadZoneProps) {
  const [dragActive, setDragActive] = useState(false)
  const [uploading, setUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true)
    else if (e.type === 'dragleave') setDragActive(false)
  }, [])

  const processFile = useCallback(async (file: File) => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ACCEPTED_TYPES.includes(ext)) {
      alert(`Nicht unterstuetztes Format: ${ext}. Erlaubt: ${ACCEPTED_TYPES.join(', ')}`)
      return
    }
    setUploading(true)
    try {
      await onUpload(file)
    } finally {
      setUploading(false)
    }
  }, [onUpload])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const file = e.dataTransfer.files?.[0]
    if (file) await processFile(file)
  }, [processFile])

  const handleClick = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const handleChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      await processFile(file)
      e.target.value = ''
    }
  }, [processFile])

  const isBusy = disabled || uploading

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        isBusy
          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
          : dragActive
            ? 'border-blue-500 bg-blue-50 cursor-pointer'
            : 'border-gray-300 hover:border-blue-400 cursor-pointer'
      }`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={!isBusy ? handleClick : undefined}
      role="button"
      tabIndex={0}
      onKeyDown={!isBusy ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() } : undefined}
      aria-label="CSV/Excel-Datei hochladen"
    >
      <UploadCloud className={`w-12 h-12 mx-auto mb-3 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} />
      <p className="text-sm font-medium text-gray-700 mb-1">
        {uploading ? 'Upload und Parsing...' : disabled ? 'Upload blockiert' : 'CSV/Excel hierher ziehen'}
      </p>
      <p className="text-xs text-gray-500 mb-2">
        oder klicken zum Auswaehlen
      </p>
      <div className="flex items-center justify-center gap-1 text-xs text-gray-400">
        <FileSpreadsheet className="w-3 h-3" />
        <span>.csv, .xlsx, .xlsm</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_MIME}
        className="hidden"
        onChange={handleChange}
        disabled={isBusy}
      />
    </div>
  )
}
